import qs from 'qs';
import { removePropertyHasEmptyValue } from '@/utils/object';

/**
 * URLクエリパラメータの付与と取得を、vue-router を経由せず window.location / history で直接行う composable。
 * 旧 mixin `@/mixins/query_parameter` の置き換え。
 *
 * `@/composable/query-parameter`(`useQueryParameter`)との使い分け:
 * - router に反応する単一キーの reactive な値が欲しい場合 → `useQueryParameter`
 * - router を経由せず URL を直接読み書きしたい場合(リード一覧のページング等) → こちら(`useUrlQueryParameter`)
 *
 * 注意: vue-routerを使うと取得できないパターンもあるので、基本的にこちらを利用する
 */
// FIXME: routerを更新しないので、routerを経由した処理をすると正しく動かないケースがある
// ページA→B→Aと同じページを連続で開こうとする場合など
// routerを仕様するとupdateが発生する問題（↓）があるため、これが解消されるまでママとする
// https://github.com/vuejs/router/issues/1701
const replaceStateWithQueryObject = (queryObject: Record<string, unknown>) => {
  // 空のものを除外したObject
  const pureQueryObject = {};
  for (const key in queryObject) {
    pureQueryObject[key] = removePropertyHasEmptyValue(queryObject[key]);
  }
  const newQuery = Object.keys(pureQueryObject).length > 0 ? `?${qs.stringify(pureQueryObject)}` : '';
  history.state.current = location.pathname + newQuery;
  if (newQuery) {
    history.replaceState(history.state, '', newQuery);
  } else {
    history.replaceState(history.state, '', location.pathname);
  }
};

// qs.parse時に使用するdecoder
// Booleanのparseなどが最適化される
// 詳細は https://github.com/ljharb/qs/issues/91#issuecomment-437926409
const parseOptionsForQs = (): qs.IParseOptions => {
  return {
    decoder(str, decoder, charset) {
      const strWithoutPlus = str.replace(/\+/g, ' ');
      if (charset === 'iso-8859-1') {
        // unescape never throws, no try...catch needed:
        return strWithoutPlus.replace(/%[0-9a-f]{2}/gi, unescape);
      }

      // MEMO: 数値のみのクエリパラメタをIntegerに変換しているが、
      // パーサに求める範囲を超えているためコメントアウトする
      // クエリパラメタに数値を入れて数値型を取り出したい場合は取り出した後で明示的にparseIntかけることとする
      // 例.) LeadList.vueのcreated内、currentDisplayNumberやcurrentPageに対する部分
      // if (/^(\d+|\d*\.\d+)$/.test(str)) {
      //   return parseFloat(str)
      // }

      const keywords = {
        // true: true,
        // false: false,
        // null: null,
        // undefined
      };
      if (str in keywords) {
        return keywords[str];
      }

      // utf-8
      try {
        return decodeURIComponent(strWithoutPlus);
      } catch (e) {
        return strWithoutPlus;
      }
    },
  };
};

const getCurrentQueryParameter = () => {
  return qs.parse(window.location.search.substring(1), parseOptionsForQs()) || {};
};

const getPageQueryParameter = (): { currentDisplayNumber?: number; currentPage?: number } => {
  const pageParams: { currentDisplayNumber?: number; currentPage?: number } = {};
  const query = qs.parse(window.location.search.substring(1), parseOptionsForQs());

  if (query.currentDisplayNumber) pageParams.currentDisplayNumber = parseInt(query.currentDisplayNumber as string);
  if (query.currentPage) pageParams.currentPage = parseInt(query.currentPage as string);
  return pageParams;
};

const getLeadViewQueryParameter = (): number => {
  const params = qs.parse(window.location.search.substring(1), parseOptionsForQs());
  return parseInt(params.leadViewId as string);
};

const getCallAnalyticsQueryParameter = () => {
  const query = qs.parse(window.location.search.substring(1), parseOptionsForQs());
  return query.callAnalytics;
};

const setQueryParameter = (queryObject: Record<string, unknown>): void => {
  const newQueryObject = {
    ...getCurrentQueryParameter(),
    ...queryObject,
  };
  replaceStateWithQueryObject(newQueryObject);
};

const deleteQueryParameter = (key: string): void => {
  const currentQueryObject = qs.parse(window.location.search.substring(1), parseOptionsForQs()) || {};

  if (currentQueryObject[key]) delete currentQueryObject[key];
  replaceStateWithQueryObject(currentQueryObject);
};

const deleteAllQueryParameters = (initialParam?: Record<string, unknown>): void => {
  const params = qs.parse(window.location.search.substring(1), parseOptionsForQs());
  if (params.leadViewId !== undefined) {
    const queryObject = { leadViewId: parseInt(params.leadViewId as string) };
    replaceStateWithQueryObject(queryObject);
  } else {
    replaceStateWithQueryObject(initialParam);
  }
};

const useUrlQueryParameter = () => {
  return {
    getCurrentQueryParameter,
    getPageQueryParameter,
    getLeadViewQueryParameter,
    getCallAnalyticsQueryParameter,
    setQueryParameter,
    deleteQueryParameter,
    deleteAllQueryParameters,
  };
};

export {
  useUrlQueryParameter,
};
